import React, { useEffect, useRef, useState } from 'react';

const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

type ScanState = 'scanning' | 'match' | 'nomatch' | 'expired' | 'error';

interface Result {
  status: string;
  name?: string;
  scan_type?: string;
  slot?: string;
  time?: string;
  message?: string;
}

export default function GymKiosk() {
  const params = new URLSearchParams(window.location.search);
  const apiKey = params.get('key') || '';
  const slot = params.get('slot') || 'Gym';
  const module = params.get('module') || 'gym';
  const isGym = module === 'gym';

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<any>(null);
  const scanLockRef = useRef(false);

  const [state, setState] = useState<ScanState>('scanning');
  const [result, setResult] = useState<Result | null>(null);
  const [faceApiLoaded, setFaceApiLoaded] = useState(false);
  const [time, setTime] = useState('');
  const [cooldown, setCooldown] = useState(0);

  // Clock
  useEffect(() => {
    const tick = () => setTime(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  // Load face-api
  useEffect(() => {
    if ((window as any).faceapi) { setFaceApiLoaded(true); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js';
    script.onload = async () => {
      const faceapi = (window as any).faceapi;
      const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.13/model';
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      setFaceApiLoaded(true);
    };
    document.head.appendChild(script);
  }, []);

  // Start camera
  useEffect(() => {
    if (!faceApiLoaded) return;
    startCamera();
    return () => stopCamera();
  }, [faceApiLoaded]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 1280, height: 720 } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      startScanning();
    } catch {
      setState('error');
    }
  };

  const stopCamera = () => {
    clearInterval(intervalRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
  };

  const startScanning = () => {
    const faceapi = (window as any).faceapi;
    if (!faceapi) return;
    intervalRef.current = setInterval(async () => {
      if (scanLockRef.current) return;
      const v = videoRef.current;
      if (!v || v.readyState < 2) return;

      scanLockRef.current = true;
      try {
        const detection = await faceapi
          .detectSingleFace(v, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks()
          .withFaceDescriptor();
        if (!detection) {
          scanLockRef.current = false;
          return;
        }
        clearInterval(intervalRef.current);

        const descriptor = Array.from(detection.descriptor);
        const res = await fetch(`${BASE}/subscriptions/kiosk-devices/face-scan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ descriptor, key: apiKey, slot }),
        });
        const data = await res.json();
        setResult(data);
        if (data.status === 'success') setState('match');
        else if (data.status === 'expired') setState('expired');
        else if (data.status === 'no_match') setState('nomatch');
        else setState('error');
      } catch {
        setState('error');
      }

      // 10 second cooldown before scanning resumes, with visible countdown
      let secondsLeft = 10;
      setCooldown(secondsLeft);
      const countdownTimer = setInterval(() => {
        secondsLeft -= 1;
        setCooldown(secondsLeft);
        if (secondsLeft <= 0) {
          clearInterval(countdownTimer);
          setCooldown(0);
          scanLockRef.current = false;
          setState('scanning');
          setResult(null);
          startScanning();
        }
      }, 1000);
    }, 800);
  };

  if (!apiKey) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        flexDirection: 'column',
        gap: 16,
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        <div style={{ fontSize: 48 }}>⚠️</div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Missing kiosk key</div>
        <div style={{ fontSize: 13, color: '#94a3b8' }}>URL must include ?key=YOUR_DEVICE_API_KEY&slot=Gym</div>
      </div>
    );
  }

  const accent = isGym ? '#7c3aed' : '#065f46';
  const accentLight = isGym ? '#a78bfa' : '#34d399';
  const icon = isGym ? '🏋️' : '🍽️';
  const title = isGym ? 'Fitness Check-In' : 'Food & Dining';

 return (
    <div style={{
      minHeight: '100vh',
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 32px',
        background: 'rgba(255,255,255,0.03)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 28 }}>{icon}</div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>{title}</div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, marginTop: 1 }}>
              {slot} · Look at the camera to mark attendance
            </div>
          </div>
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '0.05em', fontVariantNumeric: 'tabular-nums' }}>
          {time}
        </div>
      </div>

      {/* Camera area */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, gap: 40 }}>

        {/* Video */}
        <div style={{ position: 'relative', width: 560, height: 420, borderRadius: 24, overflow: 'hidden', flexShrink: 0 }}>
          <video
            ref={videoRef}
            autoPlay playsInline muted
            style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
          />

          {/* Oval overlay */}
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}>
            <div style={{
              width: 260, height: 320,
              border: `3px solid ${state === 'match' ? '#22c55e' : state === 'nomatch' || state === 'expired' ? '#ef4444' : accentLight}`,
              borderRadius: '50%',
              boxShadow: `0 0 0 9999px rgba(0,0,0,0.55)`,
              transition: 'border-color 0.3s',
            }} />
          </div>

          {/* Scanning pulse */}
          {state === 'scanning' && faceApiLoaded && (
            <div style={{
              position: 'absolute', bottom: 20, left: 0, right: 0,
              textAlign: 'center', fontSize: 12, fontWeight: 700,
              color: accentLight, letterSpacing: '0.08em',
            }}>
              ● SCANNING
            </div>
          )}

          {!faceApiLoaded && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.7)', color: '#94a3b8', fontSize: 13, fontWeight: 600,
            }}>
              Loading face recognition...
            </div>
          )}
        </div>

        {/* Result panel */}
        <div style={{ width: 300 }}>
          {state === 'scanning' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 64, marginBottom: 16 }}>👤</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
                Step up & look at the camera
              </div>
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                Face recognition marks your attendance automatically.
                No need to touch anything.
              </div>
            </div>
          )}

          {state === 'match' && result && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 80, marginBottom: 16, animation: 'pop 0.3s ease' }}>
                {result.scan_type === 'in' ? '✅' : '🚪'}
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, color: '#0f172a', marginBottom: 6, letterSpacing: '-0.02em' }}>
                {result.name}
              </div>
              <div style={{
                display: 'inline-block', padding: '6px 20px', borderRadius: 99,
                background: result.scan_type === 'in' ? 'rgba(34,197,94,0.15)' : 'rgba(251,146,60,0.15)',
                border: `1px solid ${result.scan_type === 'in' ? '#22c55e' : '#fb923c'}`,
                color: result.scan_type === 'in' ? '#22c55e' : '#fb923c',
                fontSize: 13, fontWeight: 800, marginBottom: 12, letterSpacing: '0.06em',
              }}>
                {result.scan_type === 'in' ? 'CHECKED IN' : 'CHECKED OUT'} · {result.time}
              </div>
              <div style={{ fontSize: 12, color: '#475569' }}>{result.slot}</div>
            </div>
          )}

          {state === 'nomatch' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 80, marginBottom: 16 }}>❓</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Face not recognized</div>
              <div style={{ fontSize: 13, color: '#475569' }}>Please ask staff to register your face or use QR code.</div>
            </div>
          )}

          {state === 'expired' && result && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 80, marginBottom: 16 }}>🚫</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>{result.name}</div>
              <div style={{ fontSize: 13, color: '#ef4444', fontWeight: 600 }}>Subscription expired. Please renew.</div>
            </div>
          )}

          {state === 'error' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 80, marginBottom: 16 }}>❌</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Something went wrong</div>
              <div style={{ fontSize: 13, color: '#475569' }}>Please try again or contact staff.</div>
            </div>
          )}
        {state !== 'scanning' && cooldown > 0 && (
            <div style={{ marginTop: 20, textAlign: 'center', fontSize: 13, color: '#94a3b8', fontWeight: 700 }}>
              Next scan in {cooldown}s
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: '12px 32px', textAlign: 'center',
        borderTop: '1px solid #e2e8f0',
        fontSize: 9, color: '#94a3b8', fontWeight: 700, letterSpacing: 2,
      }}>
        POWERED BY CLOUDAIRAMBO™
      </div>

      <style>{`
        @keyframes pop { 0% { transform: scale(0.5); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>
  );
}
